import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeftRight, 
  Plus,
  Check,
  X,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Trades = () => {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create trade dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fromTeamId, setFromTeamId] = useState('');
  const [toTeamId, setToTeamId] = useState('');
  const [playersOffered, setPlayersOffered] = useState([]);
  const [playersRequested, setPlayersRequested] = useState([]);
  
  // Responding
  const [responding, setResponding] = useState(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const teamsRes = await axios.get(`${API}/teams`);
      setTeams(teamsRes.data);
      
      if (user) {
        const tradesRes = await axios.get(`${API}/trades`);
        setTrades(tradesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fromTeam = teams.find(t => t.id === fromTeamId);
  const toTeam = teams.find(t => t.id === toTeamId);
  
  // Filter compatible teams (same age category)
  const compatibleTeams = fromTeam 
    ? teams.filter(t => t.id !== fromTeamId && t.age_category === fromTeam.age_category)
    : [];

  const handleCreateTrade = async () => {
    if (!fromTeamId || !toTeamId) {
      toast.error('Please select both teams');
      return;
    }
    if (playersOffered.length === 0 && playersRequested.length === 0) {
      toast.error('Please select at least one player to trade');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/trades`, {
        from_team_id: fromTeamId,
        to_team_id: toTeamId,
        players_offered: playersOffered,
        players_requested: playersRequested
      });
      toast.success('Trade proposal sent!');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to propose trade');
    } finally {
      setCreating(false);
    }
  };

  const handleRespondToTrade = async (tradeId, action) => {
    setResponding(tradeId);
    try {
      await axios.put(`${API}/trades/${tradeId}/respond`, { action });
      toast.success(`Trade ${action}ed!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} trade`);
    } finally {
      setResponding(null);
    }
  };

  const handleCancelTrade = async (tradeId) => {
    setResponding(tradeId);
    try {
      await axios.put(`${API}/trades/${tradeId}/cancel`);
      toast.success('Trade cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel trade');
    } finally {
      setResponding(null);
    }
  };

  const resetForm = () => {
    setFromTeamId('');
    setToTeamId('');
    setPlayersOffered([]);
    setPlayersRequested([]);
  };

  const togglePlayerOffered = (player) => {
    setPlayersOffered(prev => 
      prev.includes(player) ? prev.filter(p => p !== player) : [...prev, player]
    );
  };

  const togglePlayerRequested = (player) => {
    setPlayersRequested(prev => 
      prev.includes(player) ? prev.filter(p => p !== player) : [...prev, player]
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'accepted': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'cancelled': return <X className="w-4 h-4 text-gray-500" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'accepted': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'rejected': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'cancelled': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="trades-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">PLAYER TRADES</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Propose and manage player trades between teams
          </p>
        </div>
        
        {user && (
          <Button 
            className="btn-primary gap-2 mt-4 md:mt-0" 
            onClick={() => setShowCreateDialog(true)}
            data-testid="propose-trade-btn"
          >
            <Plus className="w-4 h-4" />
            Propose Trade
          </Button>
        )}
      </div>

      {/* Trades List */}
      {user ? (
        trades.length > 0 ? (
          <div className="space-y-4">
            {trades.map((trade, index) => (
              <Card 
                key={trade.id}
                className={`overflow-hidden ${trade.status === 'pending' ? 'border-yellow-500/50' : ''}`}
                data-testid={`trade-card-${index}`}
              >
                <div className={`h-1 ${
                  trade.status === 'accepted' ? 'bg-green-500' :
                  trade.status === 'rejected' ? 'bg-red-500' :
                  trade.status === 'pending' ? 'bg-yellow-500' :
                  'bg-gray-500'
                }`} />
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline" className={getStatusColor(trade.status)}>
                      {getStatusIcon(trade.status)}
                      <span className="ml-1">{trade.status.toUpperCase()}</span>
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(trade.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                    {/* From Team */}
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground mb-1">Offering</p>
                      <p className="font-bold">{trade.from_team_name}</p>
                      <div className="mt-2 space-y-1">
                        {trade.players_offered.map((player, i) => (
                          <Badge key={i} variant="secondary" className="mr-1">
                            {player}
                          </Badge>
                        ))}
                        {trade.players_offered.length === 0 && (
                          <span className="text-sm text-muted-foreground italic">No players</span>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex justify-center">
                      <ArrowLeftRight className="w-8 h-8 text-primary" />
                    </div>

                    {/* To Team */}
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground mb-1">Requesting from</p>
                      <p className="font-bold">{trade.to_team_name}</p>
                      <div className="mt-2 space-y-1">
                        {trade.players_requested.map((player, i) => (
                          <Badge key={i} variant="secondary" className="mr-1">
                            {player}
                          </Badge>
                        ))}
                        {trade.players_requested.length === 0 && (
                          <span className="text-sm text-muted-foreground italic">No players</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {trade.status === 'pending' && (
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      {trade.proposed_by === user.id ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelTrade(trade.id)}
                          disabled={responding === trade.id}
                          className="text-destructive"
                          data-testid={`cancel-trade-${index}`}
                        >
                          {responding === trade.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </>
                          )}
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleRespondToTrade(trade.id, 'accept')}
                            disabled={responding === trade.id}
                            className="btn-primary gap-1"
                            data-testid={`accept-trade-${index}`}
                          >
                            {responding === trade.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                Accept
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRespondToTrade(trade.id, 'reject')}
                            disabled={responding === trade.id}
                            className="text-destructive"
                            data-testid={`reject-trade-${index}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="text-center py-12" data-testid="no-trades">
            <CardContent>
              <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-xl font-bold mb-2">No Trades Yet</h3>
              <p className="text-muted-foreground mb-6">
                Start by proposing a trade between teams
              </p>
              <Button className="btn-primary gap-2" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4" />
                Propose Trade
              </Button>
            </CardContent>
          </Card>
        )
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">Login Required</h3>
            <p className="text-muted-foreground">
              Please login to view and manage trades
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Trade Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="create-trade-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">PROPOSE TRADE</DialogTitle>
            <DialogDescription>
              Select teams and players to trade
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Team Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Team (Offering)</Label>
                <Select value={fromTeamId} onValueChange={(v) => {
                  setFromTeamId(v);
                  setToTeamId('');
                  setPlayersOffered([]);
                  setPlayersRequested([]);
                }}>
                  <SelectTrigger data-testid="from-team-select">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} ({team.age_category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Trade With</Label>
                <Select value={toTeamId} onValueChange={(v) => {
                  setToTeamId(v);
                  setPlayersRequested([]);
                }} disabled={!fromTeamId}>
                  <SelectTrigger data-testid="to-team-select">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {compatibleTeams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} ({team.age_category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fromTeamId && compatibleTeams.length === 0 && (
                  <p className="text-xs text-destructive">No compatible teams in same age category</p>
                )}
              </div>
            </div>

            {/* Player Selection */}
            {fromTeamId && toTeamId && (
              <div className="grid grid-cols-2 gap-6">
                {/* Players to Offer */}
                <div className="space-y-2">
                  <Label>Players to Offer</Label>
                  <div className="p-4 rounded-lg border bg-muted/30 max-h-48 overflow-y-auto">
                    {fromTeam?.players?.length > 0 ? (
                      fromTeam.players.map((player, i) => (
                        <div key={i} className="flex items-center gap-2 py-2">
                          <Checkbox
                            id={`offer-${i}`}
                            checked={playersOffered.includes(player)}
                            onCheckedChange={() => togglePlayerOffered(player)}
                          />
                          <label htmlFor={`offer-${i}`} className="text-sm cursor-pointer">
                            {player}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No players on roster</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {playersOffered.length} player(s) selected
                  </p>
                </div>

                {/* Players to Request */}
                <div className="space-y-2">
                  <Label>Players to Request</Label>
                  <div className="p-4 rounded-lg border bg-muted/30 max-h-48 overflow-y-auto">
                    {toTeam?.players?.length > 0 ? (
                      toTeam.players.map((player, i) => (
                        <div key={i} className="flex items-center gap-2 py-2">
                          <Checkbox
                            id={`request-${i}`}
                            checked={playersRequested.includes(player)}
                            onCheckedChange={() => togglePlayerRequested(player)}
                          />
                          <label htmlFor={`request-${i}`} className="text-sm cursor-pointer">
                            {player}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No players on roster</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {playersRequested.length} player(s) selected
                  </p>
                </div>
              </div>
            )}

            {/* Trade Summary */}
            {(playersOffered.length > 0 || playersRequested.length > 0) && (
              <div className="p-4 rounded-lg border bg-primary/5">
                <p className="font-bold mb-2">Trade Summary</p>
                <div className="flex items-center gap-2 text-sm">
                  <span>{fromTeam?.name} sends: {playersOffered.join(', ') || 'None'}</span>
                  <ArrowLeftRight className="w-4 h-4 text-primary" />
                  <span>{toTeam?.name} sends: {playersRequested.join(', ') || 'None'}</span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTrade}
              disabled={creating || !fromTeamId || !toTeamId || (playersOffered.length === 0 && playersRequested.length === 0)}
              className="btn-primary gap-2"
              data-testid="submit-trade-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="w-4 h-4" />
                  Propose Trade
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
